import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Plus, Sparkles, ShoppingBag } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useClosetCollection } from "@/hooks/useClosetCollection";
import { track } from "@/lib/analytics";
import { supabase, type JewelryItem } from "@/lib/supabase";
import { useProfile } from "@/lib/useProfile";
import { AppShell } from "@/components/app-shell";
import { ImageBox } from "@/components/ui-extras";
import { bgColorFor } from "@/components/bg-preference-selector";
import { EmptyState } from "@/components/empty-state";
import { PhotoUpload } from "@/components/photo-upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FavoriteHeart, ItemActions } from "@/components/item-actions";
import { useCardReveal, CardRevealOverlay } from "@/components/card-reveal";

import { NearEmptyBanner } from "@/components/collection-prompts";
import { KeyboardAwareFormBody } from "@/components/keyboard-aware-form-body";
import { CustomizeViewDialog, ACCESSORY_FIELDS } from "@/components/customize-view-dialog";
import { AccessoryEditDialog } from "@/components/accessory-edit-dialog";
import { QuickAddSheet } from "@/components/quick-add-sheet";

import {
  SmartSelect,
  ACCESSORY_TYPE_PRESETS,
  JEWELRY_COLOR_PRESETS,
  ACCESSORY_MATERIAL_PRESETS,
  ACCESSORY_STORAGE_PRESETS,
  ACCESSORY_BRAND_PRESETS,
  SHARED_OCCASION_PRESETS,
} from "@/components/smart-select";
import {
  CollectionToolbar,
  applyTextSearch,
  type ActiveFilters,
  type FilterFieldDef,
  type SortOption,
} from "@/components/collection-toolbar";
import { useOwnedAccessories } from "@/lib/useCollection";

const INK = "var(--caileve-ink)";
const LILAC = "var(--caileve-lilac)";
const SOFT = "var(--caileve-soft)";
const BG = "var(--caileve-bg)";
const CLAY = "var(--caileve-copper)";

export const Route = createFileRoute("/accessories/")({
  validateSearch: (s: Record<string, unknown>): { new?: number } => ({
    new: s.new === 1 || s.new === "1" ? 1 : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Accessories — Cailève" },
      { name: "description", content: "Your curated accessories collection." },
    ],
  }),
  component: () => (
    <AppShell>
      <AccessoriesPage />
    </AppShell>
  ),
});

function AccessoriesPage() {
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const search = Route.useSearch();
  useEffect(() => {
    if (search.new === 1) setOpen(true);
  }, [search.new]);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<ActiveFilters>({});
  const [sortValue, setSortValue] = useState<string>("recent");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const { data: profile } = useProfile();
  const visibleFields = profile?.accessory_card_fields ?? ["accessory_type"];

  const { data: crossCount } = useQuery({
    queryKey: ["accessory_cross_count", uid],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { count: j } = await supabase
        .from("jewelry_items")
        .select("id", { count: "exact", head: true })
        .eq("user_id", uid)
        .eq("is_accessory", false);
      const { count: c } = await supabase
        .from("clothing_items")
        .select("id", { count: "exact", head: true })
        .eq("user_id", uid);
      return (j ?? 0) + (c ?? 0);
    },
  });

  const { data, isLoading } = useOwnedAccessories();

  const {
    wishlistItems: accessoryWishlist,
    toggleFavorite: toggleFav,
    deleteItem: del,
    promoteFromWishlist: promoteAccessoryFromWishlist,
  } = useClosetCollection({ type: "accessory", uid });

  const deletingItem = data?.find((j) => j.id === deleteId) ?? null;
  const editingItem = data?.find((j) => j.id === editId) ?? null;

  // Category counts for the tiles strip
  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    (data ?? []).forEach((j) => {
      const t = j.accessory_type ?? "Other";
      map.set(t, (map.get(t) ?? 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [data]);

  // Featured category mechanic: largest category, rotates based on day
  const featured = useMemo(() => {
    if (categoryCounts.length === 0) return null;
    const top = categoryCounts.filter((c) => c[1] >= 1);
    if (top.length === 0) return null;
    const idx = Math.floor(Date.now() / 86_400_000) % top.length;
    const [name] = top[idx];
    const items = (data ?? []).filter((j) => (j.accessory_type ?? "Other") === name).slice(0, 4);
    return { name, items };
  }, [categoryCounts, data]);

  const filterFields: FilterFieldDef[] = useMemo(() => {
    const unique = (key: keyof JewelryItem): string[] =>
      Array.from(
        new Set(
          (data ?? [])
            .map((j) => j[key])
            .filter((v): v is string => typeof v === "string" && v.length > 0),
        ),
      ).sort();
    return [
      { key: "storage_location", label: "Storage Location", type: "select", options: unique("storage_location") },
      { key: "material", label: "Material", type: "select", options: unique("material") },
      { key: "occasion", label: "Occasion", type: "select", options: unique("occasion") },
      { key: "brand", label: "Brand", type: "select", options: unique("brand") },
      { key: "accessory_type", label: "Type", type: "select", options: unique("accessory_type") },
      { key: "color", label: "Color", type: "select", options: unique("color") },
      { key: "is_favorite", label: "Favorites only", type: "toggle" },
    ];
  }, [data]);

  const sortOptions: SortOption[] = [
    { value: "recent", label: "Recently Added" },
    { value: "name_asc", label: "Name A → Z" },
    { value: "name_desc", label: "Name Z → A" },
    { value: "type", label: "Type" },
    { value: "price_asc", label: "Price Low → High" },
    { value: "price_desc", label: "Price High → Low" },
    { value: "favorites", label: "Favorites First" },
  ];

  const displayItems = useMemo(() => {
    let items = data ?? [];
    if (activeCategory) items = items.filter((j) => (j.accessory_type ?? "Other") === activeCategory);
    if (filters.is_favorite) items = items.filter((j) => j.is_favorite);
    for (const k of ["accessory_type", "color", "material", "storage_location", "occasion", "brand"] as const) {
      const v = filters[k];
      if (typeof v === "string" && v) items = items.filter((j) => j[k] === v);
    }
    items = applyTextSearch(items, searchQuery, [
      "name",
      "accessory_type",
      "color",
      "material",
      "storage_location",
      "notes",
      "brand",
      "occasion",
    ]);
    const sorted = [...items];
    const cmp = (a?: string | null, b?: string | null) => (a ?? "").localeCompare(b ?? "");
    switch (sortValue) {
      case "name_asc": sorted.sort((a, b) => cmp(a.name, b.name)); break;
      case "name_desc": sorted.sort((a, b) => cmp(b.name, a.name)); break;
      case "type": sorted.sort((a, b) => cmp(a.accessory_type?.toLowerCase().trim(), b.accessory_type?.toLowerCase().trim())); break;
      case "price_asc": sorted.sort((a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity)); break;
      case "price_desc": sorted.sort((a, b) => (b.cost ?? -Infinity) - (a.cost ?? -Infinity)); break;
      case "favorites": sorted.sort((a, b) => Number(!!b.is_favorite) - Number(!!a.is_favorite)); break;
      default: sorted.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    }
    return sorted;
  }, [data, filters, searchQuery, sortValue, activeCategory]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-end justify-between border-b border-border pb-6 mb-6">
        <div>
          <h1 className="font-serif-display text-4xl">Your Accessories</h1>
          <p className="font-serif-display italic text-base mt-1" style={{ color: "var(--caileve-lilac)" }}>The pieces Cailève styles from first.</p>
          {data && (
            <p style={{ fontFamily: "Inter, sans-serif", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", color: "var(--caileve-muted)", marginTop: 6 }}>
              {(searchQuery || Object.values(filters).some((v) => v !== undefined && v !== "" && v !== false))
                ? `${displayItems.length} of ${data.length} pieces`
                : `${data.length} ${data.length === 1 ? "piece" : "pieces"}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <CustomizeViewDialog
            userId={uid}
            column="accessory_card_fields"
            options={ACCESSORY_FIELDS}
            current={visibleFields}
            title="Customize Accessory Cards"
          />
          <Button
            type="button"
            onClick={() => setOpen(true)}
            className="bg-[var(--caileve-gold)] text-[var(--caileve-bg)] hover:bg-[var(--caileve-gold)]/90"
            style={{ borderRadius: "var(--caileve-radius-pill)" }}
          >
            <Plus className="h-4 w-4 mr-2" /> Add Piece
          </Button>
          <QuickAddSheet
            open={open}
            onOpenChange={setOpen}
            userId={uid}
            defaultType="accessory"
            onDone={() => qc.invalidateQueries({ queryKey: ["accessories", uid] })}
          />
        </div>
      </div>
      {/* Collection type switcher */}
      <div className="flex gap-2 mb-6">
        {([
          { to: "/jewelry" as const, label: "Jewelry", search: { new: undefined } as { new: number | undefined } },
          { to: "/clothing" as const, label: "Clothing", search: { new: undefined } as { new: number | undefined } },
          { to: "/accessories" as const, label: "Accessories", search: { new: undefined } as { new: number | undefined } },
        ] as const).map(({ to, label, search }) => {
          const pathname = window.location.pathname;
          const active = pathname === to || pathname.startsWith(to + "/");
          return (
            <Link
              key={to}
              to={to}
              search={search}
              className="px-4 text-sm transition-colors"
              style={
                active
                  ? {
                      backgroundColor: "transparent",
                      color: "var(--caileve-ink)",
                      borderBottom: "2px solid var(--caileve-gold)",
                      borderRadius: 0,
                      fontWeight: 600,
                      paddingBottom: 6,
                    }
                  : {
                      backgroundColor: "transparent",
                      color: "var(--caileve-muted)",
                      border: "none",
                      paddingBottom: 6,
                    }
              }
            >
              {label}
            </Link>
          );
        })}
      </div>
      {(accessoryWishlist?.length ?? 0) > 0 &&
        localStorage.getItem("caileve_considering_prompt_dismissed_v1") !== "1" && (
        <button
          type="button"
          onClick={() => {
            document.getElementById("considering-section")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="flex items-center justify-between w-full px-4 py-3 mb-4 text-left transition-opacity hover:opacity-80"
          style={{
            backgroundColor: "var(--caileve-soft)",
            borderLeft: "3px solid var(--caileve-gold)",
          }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--caileve-muted)" }}>
              Your Edit
            </p>
            <p className="font-serif-display text-lg mt-0.5" style={{ color: "var(--caileve-ink)" }}>
              {accessoryWishlist!.length} {accessoryWishlist!.length === 1 ? "piece" : "pieces"} in Your Edit
            </p>
            <p
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--caileve-muted)",
                marginTop: 2,
              }}
            >
              Pieces you are still deciding on.
            </p>
          </div>
          <span className="text-[var(--caileve-muted)] text-sm">↓</span>
        </button>
      )}


      {isLoading ? (
        <div className="mt-10">
          <CollectionSkeletonGrid count={(data as unknown[] | undefined)?.length || 8} />
        </div>
      ) : !data?.length ? (
        <div className="mt-10">
          <NearEmptyBanner kind="accessory" itemCount={0} onCta={() => setOpen(true)} crossCount={crossCount ?? 0} />
        </div>
      ) : (
        <>
          {/* Category tiles strip */}
          {categoryCounts.length > 0 && (
            <div className="mt-10 -mx-6 sm:-mx-8 px-6 sm:px-8 overflow-x-auto">
              <div className="flex gap-2 pb-2" style={{ minWidth: "min-content" }}>
                <CategoryPill
                  label="All"
                  count={data.length}
                  active={activeCategory === null}
                  onClick={() => setActiveCategory(null)}
                />
                {categoryCounts.map(([name, count]) => (
                  <CategoryPill
                    key={name}
                    label={name}
                    count={count}
                    active={activeCategory === name}
                    onClick={() => setActiveCategory(activeCategory === name ? null : name)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Featured category */}
          {featured && !activeCategory && !searchQuery && (
            <section className="mt-10">
              <button
                type="button"
                onClick={() => setActiveCategory(featured.name)}
                className="w-full text-left"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              >
                <p
                  className="uppercase tracking-[0.25em] text-[10px]"
                  style={{ color: CLAY }}
                >
                  Featured this week
                </p>
                <h2 className="font-serif-display" style={{ color: INK, fontSize: 26, marginTop: 4 }}>
                  {featured.name}
                </h2>
              </button>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {featured.items.map((j) => (
                  <Link
                    key={j.id}
                    to="/accessories/$id"
                    params={{ id: j.id }}
                    className="block group"
                  >
                    <ImageBox
                    src={j.prefer_clean_bg && j.photo_url_no_bg ? j.photo_url_no_bg : j.photo_url}
                    alt={j.name}
                    aspect="aspect-square"
                    style={j.prefer_clean_bg && j.photo_url_no_bg ? { backgroundColor: bgColorFor(j.clean_bg_color) } : undefined}
                  />
                    <p
                      className="font-serif-display mt-2 truncate"
                      style={{ color: INK, fontSize: 14, paddingBottom: 12 }}
                      title={j.name}
                    >
                      {j.name}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="mt-10">
            <CollectionToolbar
              searchPlaceholder="Search your accessories…"
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              filterFields={filterFields}
              activeFilters={filters}
              onFilterChange={(k, v) => setFilters((p) => ({ ...p, [k]: v }))}
              onClearFilters={() => setFilters({})}
              sortOptions={sortOptions}
              sortValue={sortValue}
              onSortChange={setSortValue}
            />
          </div>


          {displayItems.length === 0 ? (
            <div className="border border-dashed border-[var(--caileve-soft)] py-24 text-center">
              <p
                className="font-serif-display text-2xl"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: LILAC }}
              >
                No pieces match your search.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setFilters({});
                  setActiveCategory(null);
                }}
                className="mt-6 px-6 py-2 text-xs uppercase tracking-[0.25em] text-[var(--caileve-bg)]"
                style={{ backgroundColor: LILAC }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="collection-content grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {displayItems.map((j) => (
                <AccessoryGridCard
                  key={j.id}
                  item={j}
                  visibleFields={visibleFields}
                  onToggleFav={() => toggleFav.mutate({ id: j.id, next: !j.is_favorite })}
                  onEdit={() => setEditId(j.id)}
                  onDelete={() => setDeleteId(j.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {accessoryWishlist && accessoryWishlist.length > 0 && (
        <section id="considering-section" className="mt-16">
          <div
            style={{
              height: 1,
              backgroundColor: "var(--caileve-gold)",
              width: "100%",
              marginBottom: 32,
            }}
          />
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 24,
              color: "var(--caileve-muted)",
            }}
          >
            Your Edit
          </h2>
          <p
            style={{
              fontSize: 13,
              fontStyle: "italic",
              color: "var(--caileve-muted)",
              marginTop: 4,
              marginBottom: 24,
            }}
          >
            Pieces you're thinking about.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {accessoryWishlist.map((w) => (
              <div key={w.id}>
                <Link
                  to="/accessories/$id"
                  params={{ id: w.id }}
                  className="block cursor-pointer"
                >
                  <ImageBox src={w.photo_url} alt={w.name} />
                  <div className="mt-3">
                    <p className="font-serif-display text-xl leading-tight line-clamp-2">
                      {w.name}
                    </p>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => promoteAccessoryFromWishlist.mutate(w.id)}
                  disabled={promoteAccessoryFromWishlist.isPending}
                  style={{
                    fontSize: 11,
                    color: "var(--caileve-lilac)",
                    textDecoration: "underline",
                    background: "none",
                    border: "none",
                    padding: "6px 0 0",
                    cursor: "pointer",
                  }}
                >
                  Add to my collection
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this accessory?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingItem
                ? `"${deletingItem.name}" will be permanently removed from your accessories.`
                : "This accessory will be permanently removed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={del.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteId) {
                  del.mutate(deleteId, {
                    onSuccess: () => setDeleteId(null),
                  });
                }
              }}
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editId} onOpenChange={(o) => !o && setEditId(null)}>
        {editingItem && (
          <AccessoryEditDialog
            item={editingItem}
            userId={uid}
            onDone={() => setEditId(null)}
          />
        )}
      </Dialog>
    </div>
  );
}

function CategoryPill({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.18em] transition-colors border"
      style={{
        backgroundColor: active ? LILAC : BG,
        color: active ? "var(--caileve-bg)" : INK,
        borderColor: active ? LILAC : "rgba(155,127,168,0.3)",
      }}
    >
      <span>{label}</span>
      <span
        className="text-[10px]"
        style={{ color: active ? "rgba(255,255,255,0.8)" : LILAC }}
      >
        {count}
      </span>
    </button>
  );
}

const EMPTY_ACCESSORY = {
  name: "",
  photo_url: "",
  accessory_type: "",
  color: "",
  material: "",
  storage_location: "",
  cost: "",
  brand: "",
  occasion: "",
  notes: "",
  is_favorite: false,
};

function AddAccessoryDialog({
  open,
  userId,
  onDone,
}: {
  open: boolean;
  userId: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({ ...EMPTY_ACCESSORY });
  useEffect(() => {
    if (open) setF({ ...EMPTY_ACCESSORY });
  }, [open]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) =>
    setF((p) => ({ ...p, [k]: v }));

  const ACCESSORY_FIELD_NAMES = [
    "accessory_type",
    "jewelry_color",
    "accessory_material",
    "accessory_storage",
    "accessory_brand",
    "accessory_occasion",
  ] as const;

  const { data: accessoryFieldOptions } = useQuery({
    queryKey: ["user_field_options", userId, "__accessory_bulk__"],
    enabled: open,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_field_options")
        .select("field_name, custom_value")
        .eq("user_id", userId)
        .in("field_name", ACCESSORY_FIELD_NAMES as unknown as string[])
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      return (data ?? []) as { field_name: string; custom_value: string }[];
    },
  });

  const optionsFor = (field: string): string[] =>
    (accessoryFieldOptions ?? [])
      .filter((r) => r.field_name === field)
      .map((r) => r.custom_value);

  const typeOptions = optionsFor("accessory_type");
  const colorOptions = optionsFor("jewelry_color");
  const materialOptions = optionsFor("accessory_material");
  const storageOptions = optionsFor("accessory_storage");
  const brandOptions = optionsFor("accessory_brand");
  const occasionOptions = optionsFor("accessory_occasion");

  const m = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("jewelry_items").insert({
        user_id: userId,
        name: f.name,
        photo_url: f.photo_url || null,
        category: null,
        color: f.color || null,
        material: f.material || null,
        storage_location: f.storage_location || null,
        cost: f.cost ? Number(f.cost) : null,
        brand: f.brand || null,
        occasion: f.occasion || null,
        notes: f.notes || null,
        is_favorite: f.is_favorite,
        is_accessory: true,
        accessory_type: f.accessory_type || null,
      } as never);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Added to your collection.", {
        description: "Every great collection begins with a single piece. Yours just did.",
      });
      track("item_added", { item_type: "accessory" });
      qc.invalidateQueries({ queryKey: ["accessory_items", userId] });
      setF({ ...EMPTY_ACCESSORY });
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <DialogContent className="max-w-lg max-h-[85vh] sm:max-h-[80vh] p-0 gap-0 flex flex-col overflow-hidden">
      <DialogHeader className="shrink-0 px-6 pt-6 pb-4 border-b border-border bg-background">
        <DialogTitle className="font-serif-display text-3xl">Add Accessory</DialogTitle>
      </DialogHeader>
      <KeyboardAwareFormBody>
        <Field label="Name">
          <Input value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={120} />
        </Field>
        <Field label="Photo">
          <p
            className="italic text-center mb-2"
            style={{ fontSize: 12, color: "var(--caileve-muted)", fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Your photo is stored privately — only you can see it.
          </p>
          <PhotoUpload
            userId={userId}
            folder="accessories"
            value={f.photo_url || null}
            onChange={(p) => set("photo_url", p ?? "")}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <SmartSelect
              userId={userId}
              fieldName="accessory_type"
              presets={ACCESSORY_TYPE_PRESETS}
              value={f.accessory_type}
              onChange={(v) => set("accessory_type", v)}
              customOptions={typeOptions}
            />
          </Field>
          <Field label="Color">
            <SmartSelect
              userId={userId}
              fieldName="jewelry_color"
              presets={JEWELRY_COLOR_PRESETS}
              value={f.color}
              onChange={(v) => set("color", v)}
              customOptions={colorOptions}
            />
          </Field>
          <Field label="Material">
            <SmartSelect
              userId={userId}
              fieldName="accessory_material"
              presets={ACCESSORY_MATERIAL_PRESETS}
              value={f.material}
              onChange={(v) => set("material", v)}
              customOptions={materialOptions}
            />
          </Field>
          <Field label="Storage Location">
            <SmartSelect
              userId={userId}
              fieldName="accessory_storage"
              presets={ACCESSORY_STORAGE_PRESETS}
              value={f.storage_location}
              onChange={(v) => set("storage_location", v)}
              customOptions={storageOptions}
            />
          </Field>
          <Field label="Brand">
            <SmartSelect
              userId={userId}
              fieldName="accessory_brand"
              presets={ACCESSORY_BRAND_PRESETS}
              value={f.brand}
              onChange={(v) => set("brand", v)}
              customOptions={brandOptions}
            />
          </Field>
          <Field label="Occasion">
            <SmartSelect
              userId={userId}
              fieldName="accessory_occasion"
              presets={SHARED_OCCASION_PRESETS}
              value={f.occasion}
              onChange={(v) => set("occasion", v)}
              customOptions={occasionOptions}
            />
          </Field>
        </div>
        <Field label="Cost">
          <Input type="number" value={f.cost} onChange={(e) => set("cost", e.target.value)} />
        </Field>
        <Field label="Notes">
          <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={3} />
        </Field>
      </KeyboardAwareFormBody>
      <DialogFooter className="shrink-0 px-6 py-4 border-t border-border bg-background">
        <Button
          onClick={() => {
            if (!f.name.trim()) return toast.error("Give this piece a name to save it.");
            m.mutate();
          }}
          disabled={m.isPending}
          className="bg-[var(--caileve-lilac)] text-[var(--caileve-bg)] hover:bg-[var(--caileve-lilac)]/90"
        >
          {m.isPending ? "Saving…" : "Add to Collection"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function AccessoryGridCard({
  item: j,
  visibleFields,
  onToggleFav,
  onEdit,
  onDelete,
}: {
  item: JewelryItem;
  visibleFields: string[];
  onToggleFav: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const reveal = useCardReveal();
  const hasId = !!j.id;
  const formatField = (key: string): string | null => {
    switch (key) {
      case "accessory_type": return j.accessory_type ?? null;
      case "color": return j.color ?? null;
      case "storage_location": return j.storage_location ?? null;
      case "occasion": return j.occasion ?? null;
      case "brand": return j.brand ?? null;
      default: return null;
    }
  };
  const extras = visibleFields
    .filter((k) => k !== "accessory_type")
    .map(formatField)
    .filter((v): v is string => !!v && v.trim().length > 0);
  return (
    <div ref={reveal.rootRef} {...reveal.bind} className="group relative" style={{ border: "1px solid var(--caileve-gold-12)", boxShadow: "var(--caileve-shadow-card)", borderRadius: "var(--caileve-radius-md)" }}>
      {hasId ? (
        <Link
          to="/accessories/$id"
          params={{ id: j.id }}
          className="block cursor-pointer"
          aria-label={`View ${j.name} details`}
        >
          <AccessoryCardInner j={j} visibleFields={visibleFields} extras={extras} />
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => toast.error("This item is missing an ID and can't be opened.")}
          className="block w-full text-left cursor-pointer"
        >
          <AccessoryCardInner j={j} visibleFields={visibleFields} extras={extras} />
        </button>
      )}
      <div className="absolute top-2.5 right-2.5 z-10">
        <FavoriteHeart active={!!j.is_favorite} onToggle={onToggleFav} />
        
      </div>
      <CardRevealOverlay revealed={reveal.revealed} onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}

function AccessoryCardInner({
  j,
  visibleFields,
  extras,
}: {
  j: JewelryItem;
  visibleFields: string[];
  extras: string[];
}) {
  return (
    <>
      <ImageBox
            src={j.prefer_clean_bg && j.photo_url_no_bg ? j.photo_url_no_bg : j.photo_url}
            alt={j.name}
            style={j.prefer_clean_bg && j.photo_url_no_bg ? { backgroundColor: bgColorFor(j.clean_bg_color) } : undefined}
          />
      <div className="mt-3 pb-3 px-1">
        <p className="font-serif-display text-xl leading-tight line-clamp-2">{j.name}</p>
        {j.accessory_type && visibleFields.includes("accessory_type") && (
          <p className="uppercase mt-1" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}>
            {j.accessory_type}
          </p>
        )}
        {extras.length > 0 && (
          <p className="uppercase mt-1" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}>
            {extras.join(" · ")}
          </p>
        )}
      </div>
    </>
  );
}
function CollectionSkeletonGrid({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>
          <div
            className="animate-pulse w-full"
            style={{ aspectRatio: "1 / 1", backgroundColor: "var(--caileve-soft)" }}
          />
          <div
            className="animate-pulse mt-3"
            style={{ height: 16, width: "70%", backgroundColor: "var(--caileve-soft)" }}
          />
          <div
            className="animate-pulse mt-2"
            style={{ height: 10, width: "40%", backgroundColor: "var(--caileve-soft)" }}
          />
        </div>
      ))}
    </div>
  );
}
